import { useEffect, useRef, useState, lazy, Suspense } from 'react'
import { useTranslation } from '@/contexts/TranslationContext'

// Import Leaflet types
import type { Map as LeafletMap, LatLngExpression } from 'leaflet'

interface MapPickerProps {
  latitude?: number
  longitude?: number
  onLocationChange: (lat: number, lng: number) => void
  height?: string
  mapKey?: string // Unique key to force remount when needed
}

// Dynamically import map component (client-side only) using React lazy
const MapPickerClient = lazy(() => import('./MapPickerClient'))

const MapLoadingFallback = () => (
  <div className="w-full h-full flex items-center justify-center bg-slate-100 dark:bg-slate-800 rounded-lg">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4" />
      <p className="text-sm text-slate-600 dark:text-slate-400">Carregando mapa...</p>
    </div>
  </div>
)

export function MapPicker({ latitude, longitude, onLocationChange, height = '400px', mapKey = 'default' }: MapPickerProps) {
  const { t } = useTranslation()
  const [searchQuery, setSearchQuery] = useState('')
  const [isSearching, setIsSearching] = useState(false)
  const [searchSuccess, setSearchSuccess] = useState(false)

  const handleSearch = async () => {
    if (!searchQuery.trim()) return

    setIsSearching(true)
    setSearchSuccess(false)
    try {
      // Use Nominatim (OpenStreetMap) for geocoding - FREE!
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(searchQuery)}&format=json&limit=1&countrycodes=br`
      )
      const data = await response.json()

      if (data && data.length > 0) {
        const result = data[0]
        onLocationChange(parseFloat(result.lat), parseFloat(result.lon))
        setSearchSuccess(true)
        // Remove o feedback de sucesso após 3 segundos
        setTimeout(() => setSearchSuccess(false), 3000)
      } else {
        alert('Endereço não encontrado. Tente ser mais específico.')
      }
    } catch (error) {
      console.error('Error searching location:', error)
      alert('Erro ao buscar endereço. Tente novamente.')
    } finally {
      setIsSearching(false)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSearch()
    }
  }

  return (
    <div className="space-y-4">
      {/* Search Bar */}
      <div className="flex flex-wrap gap-2">
        <div className="flex-1 relative">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ex: Rua João Silva 123, São Paulo - SP ou simplesmente cidade/estado"
            className="w-full px-4 py-2.5 pl-10 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-slate-900 dark:text-slate-100 placeholder-slate-400"
          />
          <svg 
            className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400"
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
        <button
          type="button"
          onClick={handleSearch}
          disabled={isSearching || !searchQuery.trim()}
          className="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 dark:disabled:bg-slate-700 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors flex items-center gap-2"
        >
          {isSearching ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
              Buscando...
            </>
          ) : (
            <>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              Buscar
            </>
          )}
        </button>
        {searchSuccess && (
          <div className="flex items-center gap-2 px-4 py-2 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg animate-in fade-in slide-in-from-right-5 duration-300">
            <svg className="w-5 h-5 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span className="text-sm font-medium text-emerald-700 dark:text-emerald-300">
              Localização encontrada!
            </span>
          </div>
        )}
      </div>

      {/* Map */}
      <div style={{ height }} className="rounded-lg overflow-hidden border border-slate-300 dark:border-slate-600">
        <Suspense fallback={<MapLoadingFallback />}>
          <MapPickerClient
            latitude={latitude}
            longitude={longitude}
            onLocationChange={onLocationChange}
          />
        </Suspense>
      </div>

      {/* Coordenadas atuais */}
      {latitude !== undefined && longitude !== undefined && (
        <div className="flex items-center justify-center gap-4 p-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg">
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4 text-slate-600 dark:text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
              Latitude: <span className="font-mono">{latitude.toFixed(6)}</span>
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
              Longitude: <span className="font-mono">{longitude.toFixed(6)}</span>
            </span>
          </div>
        </div>
      )}

      {/* Instructions */}
      <div className="flex items-start gap-2 p-3 bg-emerald-50 dark:bg-emerald-900/10 border border-emerald-200 dark:border-emerald-800 rounded-lg">
        <svg className="w-5 h-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <div className="text-sm text-emerald-700 dark:text-emerald-300">
          <p className="font-medium mb-1">Como usar:</p>
          <ul className="list-disc list-inside space-y-1">
            <li>Digite um endereço e clique em "Buscar" para encontrar a localização</li>
            <li>Clique no mapa para definir ou ajustar a localização</li>
            <li>Arraste o marcador para ajustar com precisão</li>
            <li>Use o scroll do mouse para dar zoom e explorar a região</li>
          </ul>
        </div>
      </div>
    </div>
  )
}
